import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export interface CustomerApplication {
  id: number;
  product_name: string;
  edition: string;
  seat_count: number;
  status: string;
  created_at: string;
  isRenewal: boolean;
}

export interface CustomerLicense {
  id: number;
  product_name: string;
  edition: string;
  status: string;
  seat_count: number;
  activation_count: number;
  max_activations: number;
  expires_at: string | null;
}

export interface CustomerDevice {
  id: number;
  licenseId: number | null;
  machineCode: string | null;
  status: string;
  activatedAt: string | null;
  lastSeenAt: string | null;
  releasedAt: string | null;
}

export interface CustomerRiskFlag {
  key: "inactive" | "swapping";
  label: string;
  tone: "accent" | "secondary";
  detail: string;
}

export interface StaffMember {
  id: string;
  name: string;
}

export interface Customer {
  key: string;
  userId: string | null;
  name: string;
  email: string | null;
  company: string | null;
  role: string;
  status: string;
  registered: boolean;
  createdAt: string | null;
  lastActivity: number;
  applications: CustomerApplication[];
  licenses: CustomerLicense[];
  devices: CustomerDevice[];
  pendingCount: number;
  activeLicenseCount: number;
  totalSeats: number;
  releasedDeviceCount: number;
  riskFlags: CustomerRiskFlag[];
  followUp: boolean;
  followUpNote: string | null;
  followUpAssignee: string | null;
  followedAt: string | null;
}

interface ProfileRow {
  id: string;
  email: string | null;
  full_name: string | null;
  company: string | null;
  role: string;
  status: string | null;
  created_at: string | null;
  follow_up: boolean | null;
  follow_up_note: string | null;
  follow_up_assignee: string | null;
  followed_at: string | null;
}

interface ApplicationRow {
  id: number;
  user_id: string | null;
  applicant_name: string;
  email: string;
  company: string | null;
  product_name: string;
  edition: string | null;
  seat_count: number | null;
  status: string;
  created_at: string;
  renew_license_id: number | null;
}

interface LicenseRow {
  id: number;
  user_id: string | null;
  product_name: string;
  edition: string | null;
  status: string;
  seat_count: number | null;
  activation_count: number | null;
  max_activations: number | null;
  expires_at: string | null;
  issued_at: string | null;
  created_at: string;
}

interface DeviceRow {
  id: number;
  license_id: number | null;
  user_id: string | null;
  machine_code: string | null;
  status: string;
  activated_at: string | null;
  last_seen_at: string | null;
  released_at: string | null;
}

interface CustomerListResponse {
  profiles: ProfileRow[];
  applications: ApplicationRow[];
  licenses: LicenseRow[];
  devices: DeviceRow[];
  renewalReminders?: ReminderRow[];
  renewalPushes?: RenewalPush[];
  singlePushAt?: Record<string, string>;
  staff?: StaffMember[];
}

// 一次推送覆盖到的客户：用于「最近推送记录」里的「查看覆盖客户」下钻。
export interface PushCover {
  userId: string;
  name: string;
}

// 一次推送批次：同一批 upsert 共享同一 created_at，按时间戳归集出「近几次推送的时间 / 人数 / 邮件成败」。
export interface RenewalPush {
  id: string;
  createdAt: string;
  windowDays: number;
  count: number;
  emailSent: number;
  emailFailed: number;
  createdByName: string | null;
  // 这次推送覆盖到的客户名单，展开即可看清推给了谁。
  covers: PushCover[];
}

interface ReminderRow {
  user_id: string | null;
  window_days: number;
  created_at: string;
}

// 风险识别阈值：超过该天数无任何活跃即视为长期无活跃；累计解绑设备数达到该值即视为频繁换机。
const INACTIVE_DAYS = 90;
const FREQUENT_SWAP_MIN = 3;
const FREQUENT_SWAP_WINDOW_DAYS = 30;

function toTime(input: string | null | undefined): number {
  if (!input) return 0;
  const time = new Date(input).getTime();
  return Number.isNaN(time) ? 0 : time;
}

// 汇总每位客户最近一次收到续期提醒的时间，用于推送去重预览与判定。
function buildReminderMap(rows?: ReminderRow[]): Record<string, string> {
  const map: Record<string, string> = {};
  (rows ?? []).forEach((row) => {
    if (!row.user_id) return;
    const prev = map[row.user_id];
    if (!prev || new Date(row.created_at).getTime() > new Date(prev).getTime()) {
      map[row.user_id] = row.created_at;
    }
  });
  return map;
}

function aggregate(data: CustomerListResponse): Customer[] {
  const map = new Map<string, Customer>();
  const byUser = new Map<string, string>();
  const byEmail = new Map<string, string>();

  const ensure = (
    key: string,
    seed: Partial<Customer> & { name: string },
  ): Customer => {
    let entry = map.get(key);
    if (!entry) {
      entry = {
        key,
        userId: seed.userId ?? null,
        name: seed.name,
        email: seed.email ?? null,
        company: seed.company ?? null,
        role: seed.role ?? "customer",
        status: seed.status ?? "guest",
        registered: seed.registered ?? false,
        createdAt: seed.createdAt ?? null,
        lastActivity: 0,
        applications: [],
        licenses: [],
        devices: [],
        pendingCount: 0,
        activeLicenseCount: 0,
        totalSeats: 0,
        releasedDeviceCount: 0,
        riskFlags: [],
        followUp: seed.followUp ?? false,
        followUpNote: seed.followUpNote ?? null,
        followUpAssignee: seed.followUpAssignee ?? null,
        followedAt: seed.followedAt ?? null,
      };
      map.set(key, entry);
    }
    return entry;
  };

  data.profiles.forEach((profile) => {
    const key = `u:${profile.id}`;
    const entry = ensure(key, {
      userId: profile.id,
      name: profile.full_name || profile.email || "未命名客户",
      email: profile.email,
      company: profile.company,
      role: profile.role,
      status: profile.status ?? "active",
      registered: true,
      createdAt: profile.created_at,
      followUp: profile.follow_up ?? false,
      followUpNote: profile.follow_up_note,
      followUpAssignee: profile.follow_up_assignee,
      followedAt: profile.followed_at,
    });
    byUser.set(profile.id, key);
    if (profile.email) byEmail.set(profile.email.toLowerCase(), key);
  });

  data.applications.forEach((app) => {
    let key: string | null = null;
    if (app.user_id && byUser.has(app.user_id)) key = byUser.get(app.user_id) ?? null;
    else if (app.email && byEmail.has(app.email.toLowerCase())) key = byEmail.get(app.email.toLowerCase()) ?? null;
    else if (app.user_id) {
      key = `u:${app.user_id}`;
      byUser.set(app.user_id, key);
    } else {
      key = `e:${app.email.toLowerCase()}`;
    }

    const entry = ensure(key, {
      userId: app.user_id,
      name: app.applicant_name,
      email: app.email,
      company: app.company,
      role: "customer",
      status: app.user_id ? "active" : "guest",
      registered: Boolean(app.user_id),
    });

    if (!entry.company && app.company) entry.company = app.company;
    if (entry.name === "未命名客户" && app.applicant_name) entry.name = app.applicant_name;

    entry.applications.push({
      id: app.id,
      product_name: app.product_name,
      edition: app.edition ?? "standard",
      seat_count: app.seat_count ?? 1,
      status: app.status,
      created_at: app.created_at,
      isRenewal: app.renew_license_id != null,
    });
    if (app.status === "pending") entry.pendingCount += 1;
    entry.lastActivity = Math.max(entry.lastActivity, toTime(app.created_at));
  });

  data.licenses.forEach((license) => {
    if (!license.user_id) return;
    const key = byUser.get(license.user_id) ?? `u:${license.user_id}`;
    if (!byUser.has(license.user_id)) byUser.set(license.user_id, key);
    const entry = ensure(key, {
      userId: license.user_id,
      name: "未命名客户",
      registered: true,
      status: "active",
    });

    entry.licenses.push({
      id: license.id,
      product_name: license.product_name,
      edition: license.edition ?? "standard",
      status: license.status,
      seat_count: license.seat_count ?? 0,
      activation_count: license.activation_count ?? 0,
      max_activations: license.max_activations ?? 0,
      expires_at: license.expires_at,
    });
    if (license.status === "active") entry.activeLicenseCount += 1;
    entry.totalSeats += license.seat_count ?? 0;
    entry.lastActivity = Math.max(entry.lastActivity, toTime(license.created_at));
  });

  data.devices.forEach((device) => {
    if (!device.user_id) return;
    const key = byUser.get(device.user_id) ?? `u:${device.user_id}`;
    if (!byUser.has(device.user_id)) byUser.set(device.user_id, key);
    const entry = ensure(key, {
      userId: device.user_id,
      name: "未命名客户",
      registered: true,
      status: "active",
    });

    entry.devices.push({
      id: device.id,
      licenseId: device.license_id,
      machineCode: device.machine_code,
      status: device.status,
      activatedAt: device.activated_at,
      lastSeenAt: device.last_seen_at,
      releasedAt: device.released_at,
    });
    if (device.status !== "active") entry.releasedDeviceCount += 1;
    entry.lastActivity = Math.max(
      entry.lastActivity,
      toTime(device.last_seen_at),
      toTime(device.activated_at),
    );
  });

  const now = Date.now();
  const day = 86400000;
  map.forEach((entry) => {
    const flags: CustomerRiskFlag[] = [];
    if (entry.registered && entry.lastActivity > 0 && now - entry.lastActivity > INACTIVE_DAYS * day) {
      const idleDays = Math.floor((now - entry.lastActivity) / day);
      flags.push({
        key: "inactive",
        label: "长期无活跃",
        tone: "accent",
        detail: `最近一次操作在 ${idleDays} 天前（超过 ${INACTIVE_DAYS} 天）`,
      });
    }
    const recentReleased = entry.devices.filter(
      (device) =>
        device.status !== "active" &&
        device.releasedAt &&
        now - new Date(device.releasedAt).getTime() <= FREQUENT_SWAP_WINDOW_DAYS * day,
    ).length;
    if (entry.releasedDeviceCount >= FREQUENT_SWAP_MIN || recentReleased >= 2) {
      flags.push({
        key: "swapping",
        label: "频繁换机",
        tone: "secondary",
        detail: `累计解绑 ${entry.releasedDeviceCount} 台${recentReleased > 0 ? ` · 近 ${FREQUENT_SWAP_WINDOW_DAYS} 天 ${recentReleased} 台` : ""}`,
      });
    }
    entry.riskFlags = flags;
  });

  return Array.from(map.values()).sort((a, b) => {
    const scoreA = a.applications.length + a.licenses.length;
    const scoreB = b.applications.length + b.licenses.length;
    if (scoreB !== scoreA) return scoreB - scoreA;
    return b.lastActivity - a.lastActivity;
  });
}

async function invokeAdmin<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-license", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseCustomersResult {
  customers: Customer[];
  staff: StaffMember[];
  lastRemindedAt: Record<string, string>;
  recentPushes: RenewalPush[];
  lastSinglePushAt: Record<string, string>;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setStatus: (userId: string, status: "active" | "suspended") => Promise<void>;
  setFollowUp: (userId: string, followUp: boolean, note: string, assignee: string) => Promise<void>;
  bulkAssign: (userIds: string[], assignee: string) => Promise<{ assigned: number; skipped: number }>;
  pushRenewalReminders: (windowDays?: number, userId?: string) => Promise<RenewalPushResult>;
}

export interface RenewalPushResult {
  pushed: number;
  skipped: number;
  licenses: number;
  pushedLicenses: number;
  windowDays: number;
  emailsSent: number;
  emailsFailed: number;
  emailConfigured: boolean;
}

export function useCustomers(): UseCustomersResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [lastRemindedAt, setLastRemindedAt] = useState<Record<string, string>>({});
  const [recentPushes, setRecentPushes] = useState<RenewalPush[]>([]);
  const [lastSinglePushAt, setLastSinglePushAt] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setCustomers([]);
      setLastRemindedAt({});
      setRecentPushes([]);
      setLastSinglePushAt({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await invokeAdmin<CustomerListResponse>({ action: "customerList" });
      setCustomers(aggregate(data));
      setStaff(data.staff ?? []);
      setLastRemindedAt(buildReminderMap(data.renewalReminders));
      setRecentPushes(data.renewalPushes ?? []);
      setLastSinglePushAt(data.singlePushAt ?? {});
    } catch (err) {
      setError(err instanceof Error ? err.message : "客户数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const setStatus = useCallback(
    async (userId: string, status: "active" | "suspended") => {
      await invokeAdmin({ action: "setCustomerStatus", userId, status });
      await load();
    },
    [load],
  );

  const setFollowUp = useCallback(
    async (userId: string, followUp: boolean, note: string, assignee: string) => {
      await invokeAdmin({ action: "setFollowUp", userId, followUp, note, assignee });
      await load();
    },
    [load],
  );

  const bulkAssign = useCallback(
    async (userIds: string[], assignee: string) => {
      const result = await invokeAdmin<{ assigned?: number; skipped?: number }>({
        action: "assignFollowUpMany",
        userIds,
        assignee,
      });
      await load();
      return { assigned: result?.assigned ?? 0, skipped: result?.skipped ?? 0 };
    },
    [load],
  );

  const pushRenewalReminders = useCallback(
    async (windowDays = 30, userId?: string): Promise<RenewalPushResult> => {
      const result = await invokeAdmin<{
        pushed?: number;
        skipped?: number;
        licenses?: number;
        pushedLicenses?: number;
        windowDays?: number;
        emailsSent?: number;
        emailsFailed?: number;
        emailConfigured?: boolean;
      }>({ action: "pushRenewalReminders", windowDays, userId });
      const mapped: RenewalPushResult = {
        pushed: result?.pushed ?? 0,
        skipped: result?.skipped ?? 0,
        licenses: result?.licenses ?? 0,
        pushedLicenses: result?.pushedLicenses ?? 0,
        windowDays: result?.windowDays ?? windowDays,
        emailsSent: result?.emailsSent ?? 0,
        emailsFailed: result?.emailsFailed ?? 0,
        emailConfigured: Boolean(result?.emailConfigured),
      };
      // 推送后刷新提醒历史，让弹窗预览里的「本周期已提醒」跳过数即时生效。
      await load();
      return mapped;
    },
    [load],
  );

  return {
    customers,
    staff,
    lastRemindedAt,
    recentPushes,
    lastSinglePushAt,
    loading: loading || authLoading,
    error,
    reload: load,
    setStatus,
    setFollowUp,
    bulkAssign,
    pushRenewalReminders,
  };
}